import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import {
    useDeleteEmployeeMutation,
    useEditUserEmployeeMutation,
    useGetEmployeeByIdQuery,
} from '../../Features/userEmployeesSlice';
import { useGetUserEmployeeRolesByUserIdQuery } from '../../Features/usersSlice';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useTheme } from '../../ThemeContext';

import {
    UserIcon,
    MailIcon,
    PhoneIcon,
    LockClosedIcon,
    BriefcaseIcon,
    ArrowLeftIcon,
    TrashIcon,
    PencilIcon,
} from '@heroicons/react/outline';

function UserEmployeeEditView() {
    const navigate = useNavigate();
    const { user_id } = useParams();
    const currentUser = useSelector(selectCurrentUser);
    const { darkMode } = useTheme();
    const { isOnline } = useIsOnline();

    const [input, setInput] = useState({});
    const [progress, setProgress] = useState(0);

    const [errors, setErrors] = useState({
        email: '',
        phone: '',
        fname: '',
        lname: '',
        password: '',
        role_id: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const {
        data: user,
        isLoading,
        isSuccess,
        isError,
    } = useGetEmployeeByIdQuery(user_id);

    const {
        data: employeeRoles,
        isSuccess: isSuccessEmployeeRoles,
        isLoading: isLoadingEmployeeRoles,
    } = useGetUserEmployeeRolesByUserIdQuery(currentUser.id);

    useEffect(() => {
        setProgress(40);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess && user?.data) {
            setInput(user.data);
            setProgress(100);
        }
    }, [user, isLoading, isSuccess]);

    const [editUserProfile, { isLoading: isSaving }] =
        useEditUserEmployeeMutation();

    const [deleteUser, { isLoading: isDeleting }] =
        useDeleteEmployeeMutation();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        try {
            setProgress(80);

            const resp = await editUserProfile(input).unwrap();

            if (resp?.success === true) {
                setProgress(100);
                return navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.errors) {
                setErrors({
                    fname: err.data.errors.fname || '',
                    lname: err.data.errors.lname || '',
                    email: err.data.errors.email || '',
                    phone: err.data.errors.phone || '',
                    role_id: err.data.errors.role_id || '',
                    password: err.data.errors.password || '',
                });
            } else if (err?.data?.error) {
                return swal(
                    'Update Failed',
                    err.data.error,
                    'error'
                );
            } else {
                return swal(
                    'Update Failed',
                    'Something went wrong. Please try again.',
                    'error'
                );
            }
        }
    };

    const handDeleteUser = async (e, id) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        const confirmed = window.confirm(
            'Are you sure you want to delete this user?'
        );

        if (!confirmed) {
            return;
        }

        try {
            setProgress(80);

            const resp = await deleteUser(user_id).unwrap();

            if (resp?.success === true) {
                setProgress(100);
                return navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            swal(
                'Error',
                err?.data?.error || err?.message || 'Unable to delete employee',
                'error'
            );
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    const inputClass =
        'w-full px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-white border border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-1 focus:ring-[#80b643] outline-none transition';

    if (isLoading) {
        return (
            <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
                <LoadingBar
                    color="#80b643"
                    height="4px"
                    progress={progress}
                    onLoaderFinished={() => setProgress(0)}
                />

                <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <UserIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Edit Employee
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Loading employee information...
                            </p>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-center py-16">
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Loading employee...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
                <LoadingBar
                    color="#80b643"
                    height="4px"
                    progress={progress}
                    onLoaderFinished={() => setProgress(0)}
                />

                <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <UserIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Edit Employee
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Employee information could not be loaded
                            </p>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
                        <div className="flex flex-col items-center justify-center text-center py-12">
                            <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <UserIcon className="w-7 h-7 text-gray-400" />
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Employee not available
                            </h3>

                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                                We could not retrieve this employee's information.
                                Please go back and try again.
                            </p>

                            <button
                                type="button"
                                onClick={back}
                                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200"
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                {/* PAGE HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <PencilIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Edit Employee
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Update employee account information
                            </p>
                        </div>
                    </div>
                </div>

                {/* MAIN FORM CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <UserIcon className="w-5 h-5 text-[#80b643]" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Employee Information
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Update the employee details below
                                </p>
                            </div>
                        </div>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        className="p-4 sm:p-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* FIRST NAME */}
                            <div>
                                <label
                                    htmlFor="fname"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <UserIcon className="w-4 h-4 text-[#80b643]" />
                                    First name
                                </label>

                                <input
                                    type="text"
                                    name="fname"
                                    id="fname"
                                    value={input.fname || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter first name"
                                />

                                {errors.fname && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.fname}
                                    </p>
                                )}
                            </div>

                            {/* LAST NAME */}
                            <div>
                                <label
                                    htmlFor="lname"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <UserIcon className="w-4 h-4 text-[#80b643]" />
                                    Last name
                                </label>

                                <input
                                    type="text"
                                    name="lname"
                                    id="lname"
                                    value={input.lname || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter last name"
                                />

                                {errors.lname && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.lname}
                                    </p>
                                )}
                            </div>

                            {/* EMAIL */}
                            <div>
                                <label
                                    htmlFor="email"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <MailIcon className="w-4 h-4 text-[#80b643]" />
                                    Email
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={input.email || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter email address"
                                />

                                {errors.email && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* PHONE */}
                            <div>
                                <label
                                    htmlFor="phone"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <PhoneIcon className="w-4 h-4 text-[#80b643]" />
                                    Phone number
                                </label>

                                <input
                                    type="text"
                                    name="phone"
                                    id="phone"
                                    value={input.phone || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter phone number"
                                />

                                {errors.phone && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                            {/* ROLE */}
                            <div>
                                <label
                                    htmlFor="role_id"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <BriefcaseIcon className="w-4 h-4 text-[#80b643]" />
                                    Role
                                </label>

                                <select
                                    value={input.role_id || ''}
                                    name="role_id"
                                    id="role_id"
                                    onChange={handleChange}
                                    disabled={isLoadingEmployeeRoles}
                                    className={`${inputClass} ${
                                        isLoadingEmployeeRoles
                                            ? 'opacity-60 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <option value="">
                                        {isLoadingEmployeeRoles
                                            ? 'Loading roles...'
                                            : 'Select role'}
                                    </option>

                                    {isSuccessEmployeeRoles &&
                                        employeeRoles?.data?.map((data) => (
                                            <option
                                                key={data.id}
                                                value={data.id}
                                            >
                                                {data.name}
                                            </option>
                                        ))}
                                </select>

                                {errors.role_id && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.role_id}
                                    </p>
                                )}
                            </div>

                            {/* PASSWORD */}
                            <div>
                                <label
                                    htmlFor="password"
                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    <LockClosedIcon className="w-4 h-4 text-[#80b643]" />
                                    Password
                                </label>

                                <input
                                    type="text"
                                    name="password"
                                    id="password"
                                    value={input.password || ''}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder="Enter password"
                                />

                                {errors.password && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.password}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* DELETE EMPLOYEE */}
                        <div className="mt-8 border border-red-200 dark:border-red-900/50 rounded-xl overflow-hidden">
                            <div className="p-4 sm:p-5 bg-red-50/50 dark:bg-red-950/20">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                            <TrashIcon className="w-5 h-5 text-red-500" />
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                Delete employee
                                            </h3>

                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Permanently delete this employee
                                                account and its associated access.
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) =>
                                            handDeleteUser(e, input.id)
                                        }
                                        disabled={isDeleting || isSaving}
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-200 dark:border-red-900/60 bg-white dark:bg-gray-900 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 text-sm font-medium transition-all duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <TrashIcon className="w-4 h-4" />

                                        {isDeleting
                                            ? 'Deleting...'
                                            : 'Delete Employee'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ACTIONS */}
                        <div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={isSaving || isDeleting}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 w-full sm:w-auto disabled:opacity-50"
                                >
                                    <ArrowLeftIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isSaving || isDeleting}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? (
                                        <>
                                            <svg
                                                className="animate-spin h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />

                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                />
                                            </svg>

                                            Saving...
                                        </>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default UserEmployeeEditView;